import cameraDarkIcon from "@/assets/ai-chat-bar/camera-dark.svg";
import cameraLightIcon from "@/assets/ai-chat-bar/camera-light.svg";
import cancelIcon from "@/assets/ai-chat-bar/cancel.svg";
import closeIcon from "@/assets/ai-chat-bar/close.svg";
import micDarkIcon from "@/assets/ai-chat-bar/mic-dark.svg";
import micLightIcon from "@/assets/ai-chat-bar/mic-light.svg";
import sendDarkIcon from "@/assets/ai-chat-bar/send-dark.svg";
import sendLightIcon from "@/assets/ai-chat-bar/send-light.svg";
import menuCameraIcon from "@/assets/ai-chat-menu/camera.svg";
import menuPhotosIcon from "@/assets/ai-chat-menu/photos.svg";
import { ApiError } from "@/clients/core/ApiError";
import { CancelError } from "@/clients/core/CancelablePromise";
import { TranscribeService } from "@/clients/services/TranscribeService";
import { LoadingSpinnerIcon } from "@/components/icons/loading-spinner";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { MAX_RECORDING_MS, useAudioRecorder } from "@/features/ai-chat/hooks/use-audio-recorder";
import {
  MAX_CHAT_IMAGES,
  appendChatImage,
  appendChatImages,
} from "@/features/ai-chat/utils/chat-image";
import { useToast } from "@/hooks/ui/use-toast";
import { usePhotoPicker } from "@/hooks/utils/use-photo-picker";
import { pendingChatImageAtom, type ChatMessageImage } from "@/stores/chat.store";
import { cn } from "@/utils/cn";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { captureException } from "@sentry/react";
import { useAtom } from "jotai";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

const RecordingDotIcon = () => (
  <span aria-hidden className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
);

const MAX_DURATION_LABEL = formatDuration(MAX_RECORDING_MS);
const WARNING_THRESHOLD_MS = MAX_RECORDING_MS - 10_000;
const MIC_PULSE_DELAY_MS = 600;

const filenameForMimeType = (mimeType: string): string => {
  const base = mimeType.split(";")[0].trim().toLowerCase();
  switch (base) {
    case "audio/m4a":
    case "audio/x-m4a":
    case "audio/mp4":
      return "voice.m4a";
    case "audio/webm":
      return "voice.webm";
    case "audio/ogg":
      return "voice.ogg";
    case "audio/wav":
    case "audio/x-wav":
      return "voice.wav";
    default:
      return "voice.m4a";
  }
};

type Mode = "idle" | "recording" | "transcribing";

export interface ChatInputBarHandle {
  /** Triggers mic recording — same flow as tapping the in-pill mic button. */
  startMic: () => Promise<void>;
  /** Sets the textarea value and focuses it with cursor at the end. */
  setTextAndFocus: (text: string) => void;
}

interface ChatInputBarProps {
  onSubmit: (text: string, images: ChatMessageImage[]) => Promise<boolean>;
  disabled?: boolean;
  attachmentsDisabled?: boolean;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  /** "dark" = meal-picker greeting over dark bg. "default" = regular light chat. */
  variant?: "default" | "dark";
  isGenerating?: boolean;
  isStopping?: boolean;
  onStop?: () => void;
}

export const ChatInputBar = memo(
  forwardRef<ChatInputBarHandle, ChatInputBarProps>(function ChatInputBar(
    {
      onSubmit,
      disabled,
      attachmentsDisabled = false,
      placeholder = "Something else...",
      onValueChange,
      variant = "default",
      isGenerating = false,
      isStopping = false,
      onStop,
    }: ChatInputBarProps,
    ref,
  ) {
    const isDark = variant === "dark";
    const [value, setValue] = useState("");
    const [pendingImages, setPendingImages] = useAtom(pendingChatImageAtom);
    const { takePhoto, pickFromGallery, isOpening } = usePhotoPicker();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { showError } = useToast();

    const [mode, setMode] = useState<Mode>("idle");
    const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
    const inFlightRef = useRef<{ cancel: () => void } | null>(null);
    const lastDurationRef = useRef(0);
    const [pulseMic, setPulseMic] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => setPulseMic(true), MIC_PULSE_DELAY_MS);
      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      onValueChange?.(value);
    }, [value, onValueChange]);

    const reportTranscribeFailure = useCallback(
      (
        err: unknown,
        ctx: { durationMs: number; latencyMs: number; mimeType: string; blobSize: number },
      ) => {
        const exception = err instanceof Error ? err : new Error(String(err));
        const httpStatus = err instanceof ApiError ? err.status : undefined;

        captureException(exception, {
          tags: {
            feature: "ai-chat",
            phase: "transcribe",
            errorName: exception.name,
            ...(httpStatus !== undefined ? { httpStatus: String(httpStatus) } : {}),
          },
          extra: {
            durationMs: ctx.durationMs,
            latencyMs: ctx.latencyMs,
            mimeType: ctx.mimeType,
            blobSize: ctx.blobSize,
          },
        });

        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_TRANSCRIBE_FAILED, {
          duration_ms: ctx.durationMs,
          latency_ms: ctx.latencyMs,
          mime_type: ctx.mimeType,
          blob_size: ctx.blobSize,
          ...(httpStatus !== undefined ? { http_status: httpStatus } : {}),
        });
      },
      [],
    );

    const sendMessage = useCallback(
      async (text: string, images: ChatMessageImage[]) => {
        if (disabled) return;
        if (text.trim().length === 0 && images.length === 0) return;
        setValue("");
        const succeeded = await onSubmit(text, images);
        if (succeeded) {
          setPendingImages((current) => (current === images ? [] : current));
        }
      },
      [disabled, onSubmit, setPendingImages],
    );

    const runTranscribe = useCallback(
      async (audio: { blob: Blob; mimeType: string; durationMs: number; autoStopped: boolean }) => {
        lastDurationRef.current = audio.durationMs;
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_RECORD_CONFIRMED, {
          duration_ms: audio.durationMs,
          auto_stopped: audio.autoStopped,
        });

        setMode("transcribing");
        const startedAt = Date.now();
        const audioFile = new File([audio.blob], filenameForMimeType(audio.mimeType), {
          type: audio.mimeType,
        });
        const request = TranscribeService.transcribeAudioApiV1TranscribePost({ audio: audioFile });
        inFlightRef.current = request;

        try {
          const response = await request;
          const text = response.data.text;
          const latencyMs = Date.now() - startedAt;
          const trimmed = text.trim();
          const autoSent = trimmed.length > 0 && !audio.autoStopped;
          trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_TRANSCRIBE_SUCCEEDED, {
            duration_ms: audio.durationMs,
            transcript_length: text.length,
            latency_ms: latencyMs,
            mime_type: audio.mimeType,
            blob_size: audio.blob.size,
            auto_sent: autoSent,
          });

          const combined = value ? `${value} ${trimmed}` : trimmed;
          setMode("idle");

          // The user already tapped send to end the recording, so skip the second
          // tap and send the transcript straight away — along with anything typed
          // or attached beforehand. A recording cut off at MAX_RECORDING_MS is
          // likely mid-sentence, so that one still lands in the field to finish.
          if (autoSent) {
            void sendMessage(combined, pendingImages);
            return;
          }

          setValue(combined);
          requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (el) {
              el.focus();
              const end = el.value.length;
              el.setSelectionRange(end, end);
            }
          });
        } catch (err) {
          if (err instanceof CancelError) {
            return;
          }
          const latencyMs = Date.now() - startedAt;
          reportTranscribeFailure(err, {
            durationMs: audio.durationMs,
            latencyMs,
            mimeType: audio.mimeType,
            blobSize: audio.blob.size,
          });
          showError("Couldn't transcribe that — please try again.");
          setMode("idle");
        } finally {
          inFlightRef.current = null;
        }
      },
      [pendingImages, reportTranscribeFailure, sendMessage, showError, value],
    );

    const recorder = useAudioRecorder({ onAutoStop: runTranscribe });

    const canSend = (value.trim().length > 0 || pendingImages.length > 0) && !disabled;
    const showMic = mode === "idle" && value.trim().length === 0 && pendingImages.length === 0;

    useLayoutEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      // Clamp to [42px, 160px]. Lower bound guards against the modal present-transition
      // where scrollHeight reads 0 and the field collapses to zero height.
      el.style.height = `${Math.max(Math.min(el.scrollHeight, 160), 42)}px`;
    }, [value]);

    const handleSend = async () => {
      if (!canSend) return;
      await sendMessage(value, pendingImages);
    };

    const handleCameraPicked = async () => {
      const picked = await takePhoto();
      if (!picked) return;
      try {
        setPendingImages((current) => appendChatImage(current, picked));
      } catch (error) {
        showError(error instanceof Error ? error.message : "Couldn't add this image.");
        return;
      }
      trackMixpanelEvent(MixpanelEvents.AI_CHAT_ATTACHMENT_ADDED, { source: "camera" });
    };

    const handleLibraryPicked = async () => {
      const remaining = MAX_CHAT_IMAGES - pendingImages.length;
      if (remaining <= 0) return;
      const picked = await pickFromGallery(remaining);
      if (picked.length === 0) return;
      try {
        setPendingImages((current) => appendChatImages(current, picked));
      } catch (error) {
        showError(error instanceof Error ? error.message : "Couldn't add these images.");
        return;
      }
      trackMixpanelEvent(MixpanelEvents.AI_CHAT_ATTACHMENT_ADDED, {
        source: "library",
        count: picked.length,
      });
    };

    const handleAttachClick = () => {
      setIsAttachMenuOpen((open) => !open);
    };

    const handleMenuCamera = () => {
      setIsAttachMenuOpen(false);
      void handleCameraPicked();
    };

    const handleMenuPhotos = () => {
      setIsAttachMenuOpen(false);
      void handleLibraryPicked();
    };

    const handleRemoveImage = (index: number) => {
      setPendingImages((current) => current.filter((_, currentIndex) => currentIndex !== index));
      trackMixpanelEvent(MixpanelEvents.AI_CHAT_ATTACHMENT_REMOVED);
    };

    const handleMicClick = useCallback(async () => {
      if (disabled) return;
      const result = await recorder.start();
      if (result === "started") {
        setMode("recording");
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_RECORD_STARTED);
        return;
      }
      if (result === "permission_denied") {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_PERMISSION_DENIED);
        showError("Microphone access denied. Enable it in Settings to dictate.");
        return;
      }
      showError("Voice recording isn't supported on this device.");
    }, [disabled, recorder, showError]);

    useImperativeHandle(
      ref,
      () => ({
        startMic: handleMicClick,
        setTextAndFocus: (text: string) => {
          setValue(text);
          requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (el) {
              el.focus();
              const end = el.value.length;
              el.setSelectionRange(end, end);
            }
          });
        },
      }),
      [handleMicClick],
    );

    const handleConfirmRecording = async () => {
      const audio = await recorder.stop();
      if (!audio) {
        setMode("idle");
        return;
      }
      await runTranscribe(audio);
    };

    const handleCancel = async () => {
      if (mode === "recording") {
        const duration = recorder.durationMs;
        await recorder.cancel();
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_RECORD_CANCELLED, {
          duration_ms: duration,
          phase: "recording",
        });
        setMode("idle");
        return;
      }
      if (mode === "transcribing") {
        inFlightRef.current?.cancel();
        inFlightRef.current = null;
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_VOICE_RECORD_CANCELLED, {
          duration_ms: lastDurationRef.current,
          phase: "transcribing",
        });
        setMode("idle");
      }
    };

    // Shared pill base — border + theme bg. Dark (meal-picker greeting / engaged
    // chat) uses the near-pill rounded-[34px] radius per Figma node 632:453; the
    // light chat keeps rounded-[18px] (Figma node 525:3692).
    const pillBase = cn(
      "flex border border-[rgba(151,87,238,0.76)]",
      isDark ? "rounded-[34px] bg-[rgba(0,0,0,0.22)]" : "rounded-[18px] bg-white",
    );

    // Divider — 1px wide, 29px tall, purple tint (Figma: bg-[#af7df2] opacity-40).
    const divider = (
      <div
        aria-hidden
        className={cn(
          "h-[29px] w-px shrink-0 rounded-[1px]",
          isDark ? "bg-[#34275f]" : "bg-[#af7df2]/40",
        )}
      />
    );

    const renderIdlePill = () => (
      <div className={cn(pillBase, "min-h-[52px] items-center pr-0 pr-[2px] pl-[13px]")}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            "flex-1 resize-none border-none bg-transparent outline-none",
            "mr-1 max-h-40 overflow-y-auto py-2.5 leading-snug",
            "text-[14px] font-medium",
            isDark
              ? "text-white placeholder:font-medium placeholder:text-[#e9dfff]"
              : "placeholder:font-medium placeholder:text-black/60",
          )}
          style={{
            lineHeight: "23px",
            scrollbarWidth: "none",
            fontFamily: "var(--font-sans-serif)",
          }}
          aria-label="Message"
        />

        <div className="flex shrink-0 items-center">
          {divider}
          {isGenerating ? (
            <button
              type="button"
              aria-label={isStopping ? "Stopping response" : "Stop generating"}
              onClick={onStop}
              disabled={isStopping}
              className="flex size-[44px] shrink-0 items-center justify-center active:opacity-70 disabled:opacity-50"
            >
              {isStopping ? (
                <LoadingSpinnerIcon className={isDark ? "text-white" : "text-[#620ad5]"} />
              ) : (
                <span
                  aria-hidden
                  className={cn("size-3 rounded-[2px]", isDark ? "bg-white" : "bg-[#620ad5]")}
                />
              )}
            </button>
          ) : showMic ? (
            <button
              type="button"
              aria-label="Record voice message"
              onClick={handleMicClick}
              disabled={disabled}
              className={cn(
                "flex size-[44px] shrink-0 items-center justify-center active:opacity-70 disabled:opacity-50",
                pulseMic && "animate-mic-pulse-twice",
              )}
            >
              <img src={isDark ? micDarkIcon : micLightIcon} alt="" className="size-6" />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Send message"
              onClick={handleSend}
              disabled={!canSend}
              className="flex size-[44px] shrink-0 items-center justify-center active:opacity-70 disabled:opacity-50"
            >
              <img src={isDark ? sendDarkIcon : sendLightIcon} alt="" className="size-6" />
            </button>
          )}
        </div>
      </div>
    );

    // Recording / transcribing state — fixed 52px height, items centred (matches Figma dictating).
    const renderRecordingStrip = () => {
      const isRecording = mode === "recording";
      const overWarning = isRecording && recorder.durationMs >= WARNING_THRESHOLD_MS;
      return (
        <div className={cn(pillBase, "min-h-[52px] items-center pr-0 pr-[2px] pl-[13px]")}>
          <button
            type="button"
            aria-label={isRecording ? "Cancel recording" : "Cancel transcription"}
            onClick={handleCancel}
            className="flex shrink-0 items-center justify-center"
          >
            <img src={cancelIcon} alt="" className="size-6" />
          </button>

          <div className="mx-[18px] flex flex-1 items-center">
            {isRecording ? (
              <>
                <span
                  className={cn(
                    "text-[14px]",
                    isDark ? "text-[rgba(233,223,255,0.6)]" : "text-[rgba(0,0,0,0.6)]",
                  )}
                >
                  Listening...
                </span>
                <div className="ml-auto flex items-center gap-[6px]">
                  <RecordingDotIcon />
                  <span
                    className={cn(
                      "text-[14px] tabular-nums",
                      overWarning
                        ? "text-amber-500"
                        : isDark
                          ? "text-[rgba(233,223,255,0.6)]"
                          : "text-[rgba(0,0,0,0.6)]",
                    )}
                  >
                    {formatDuration(recorder.durationMs)} / {MAX_DURATION_LABEL}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center gap-2">
                <LoadingSpinnerIcon className={isDark ? "text-[#e9dfff]" : "text-gray-500"} />
                <span className={cn("text-[14px]", isDark ? "text-white" : "text-gray-600")}>
                  Transcribing…
                </span>
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center">
            {divider}
            {isRecording ? (
              <button
                type="button"
                aria-label="Send voice message"
                onClick={handleConfirmRecording}
                className="flex size-[50px] shrink-0 items-center justify-center active:opacity-70"
              >
                <img src={isDark ? sendDarkIcon : sendLightIcon} alt="" className="size-6" />
              </button>
            ) : (
              <div className="size-[50px] shrink-0" aria-hidden />
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="relative mx-5 mb-4 flex flex-col gap-2">
        {mode === "idle" && pendingImages.length > 0 && (
          <div className="flex gap-2">
            {pendingImages.map((image, index) => (
              <div className="relative" key={`${image.dataUrl.slice(-24)}-${index}`}>
                <img
                  src={image.dataUrl}
                  alt={`Pending attachment ${index + 1}`}
                  className="h-16 w-16 rounded-xl object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  aria-label={`Remove attachment ${index + 1}`}
                  className={cn(
                    "absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center",
                    "rounded-full bg-black shadow",
                  )}
                >
                  <img src={closeIcon} alt="" className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-row-reverse items-center gap-1">
          <div className="relative min-w-0 flex-1">
            {mode === "idle" ? renderIdlePill() : renderRecordingStrip()}
          </div>

          {/* Camera sits outside the pill; its menu is anchored to the right. */}
          <div className="relative flex shrink-0 items-center">
            <button
              type="button"
              aria-label="Add attachment"
              onClick={handleAttachClick}
              disabled={
                disabled ||
                attachmentsDisabled ||
                isOpening ||
                pendingImages.length >= MAX_CHAT_IMAGES ||
                mode !== "idle"
              }
              className="flex size-[44px] shrink-0 items-center justify-center active:opacity-70 disabled:opacity-50"
            >
              <img src={isDark ? cameraDarkIcon : cameraLightIcon} alt="" className="size-6" />
            </button>

            {isAttachMenuOpen && mode === "idle" && (
              <>
                <div
                  aria-hidden
                  onClick={() => setIsAttachMenuOpen(false)}
                  className="fixed inset-0 z-40 bg-black/40"
                />
                <div
                  role="menu"
                  className={cn(
                    "absolute bottom-[54px] left-[-6px] z-50 h-[104px] w-[180px]",
                    "overflow-hidden rounded-[20px] border border-white bg-white/95",
                    "shadow-[0_8px_24px_rgba(0,0,0,0.10)]",
                  )}
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleMenuCamera}
                    className="flex h-[52px] w-full items-center gap-3 px-3 py-2.5 text-left active:bg-black/5"
                  >
                    <img src={menuCameraIcon} alt="" aria-hidden className="h-8 w-8" />
                    <span className="text-[14px] text-black">Camera</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleMenuPhotos}
                    className="flex h-[52px] w-full items-center gap-3 px-3 py-2.5 text-left active:bg-black/5"
                  >
                    <img src={menuPhotosIcon} alt="" aria-hidden className="h-8 w-8" />
                    <span className="text-[14px] text-black">Photos</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }),
);
